/**
 * E03-F08-T07 — strip file-storage addresses and signed links out of document text.
 *
 * Why: attached files live in object storage (R2 / S3 / Drive) behind long signed
 * urls. Those urls used to travel into the Kickoff Sheet — the legacy
 * `manualFiles` / `jd_copy` format was "name: url", and the model echoed what it
 * was given — so the team saw `image001.png: https://…r2.cloudflarestorage.com/…
 * ?X-Amz-Signature=…` where the client's JD should have been. A storage url is
 * never document content (and a signed one is a credential that expires), so the
 * rule is simple: a document never shows one.
 *
 * Only STORAGE / SIGNED urls are removed. An ordinary public link (a careers page
 * the client pointed at) is content and stays.
 *
 * Pure and DOM-free: used by the api when it writes the Kickoff Sheet and by both
 * renderers (Word export + on-screen view), so sheets written before this rule
 * render clean too.
 */
/** Hosts / query markers that identify a storage or signed url. */
const STORAGE_URL_MARKERS = [
    /r2\.cloudflarestorage\.com/i,
    /\.r2\.dev\b/i,
    /\bamazonaws\.com/i,
    /storage\.googleapis\.com/i,
    /drive\.google\.com/i,
    /docs\.google\.com\/\S*\/d\//i,
    /googleusercontent\.com/i,
    /[?&]X-Amz-(Signature|Credential|Algorithm)=/i,
    /[?&]sig=/i,
];
/** True when `url` points into file storage or carries a signature. */
export function isStorageUrl(url) {
    return STORAGE_URL_MARKERS.some((re) => re.test(url));
}
const NL = '\n';
const HAS_URL = /https?:\/\//i;
const MD_LINK = /\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/gi;
const HTML_LINK = /<a\b[^>]*\bhref\s*=\s*["'](https?:\/\/[^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
// optional "name: " lead-in + optional opening bracket + the url + optional closer
const BARE_URL = /(\s*:\s*)?[[(<]?(https?:\/\/[^\s<>"'`)\]]+)[\])>]?/gi;
/** Remove storage urls from ONE line; `null` when the line held nothing else. */
function scrubLine(line) {
    if (!HAS_URL.test(line))
        return line;
    let out = line.replace(MD_LINK, (m, label, url) => isStorageUrl(url) ? label : m);
    out = out.replace(HTML_LINK, (m, url, label) => (isStorageUrl(url) ? label : m));
    out = out.replace(BARE_URL, (m, _sep, url) => isStorageUrl(url) ? '' : m);
    if (out === line)
        return line;
    return out.trim() === '' ? null : out.replace(/\s+$/, '');
}
/**
 * Remove storage/signed urls from a text value (markdown, plain text or HTML).
 *   - `[label](storage-url)` → `label`;
 *   - `<a href="storage-url">label</a>` → `label`;
 *   - a bare storage url (and a `: ` that introduced it, as in "name: url") → removed.
 * A line left empty by the removal is dropped. Non-storage urls are untouched.
 */
export function scrubStorageLinks(text) {
    if (!text || !HAS_URL.test(text))
        return text;
    return text
        .split(NL)
        .map(scrubLine)
        .filter((line) => line !== null)
        .join(NL);
}
/** Recursively apply {@link scrubStorageLinks} to every string inside `value`. */
export function scrubStorageLinksDeep(value) {
    if (typeof value === 'string')
        return scrubStorageLinks(value);
    if (Array.isArray(value))
        return value.map((v) => scrubStorageLinksDeep(v));
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = scrubStorageLinksDeep(v);
        }
        return out;
    }
    return value;
}
//# sourceMappingURL=storageLinks.js.map